import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';

const URGENCY_COLOR = { high: '#E96A6A', medium: '#f59e0b', low: '#aaa' };

function NudgeWarningIcon({ color = '#E96A6A', size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0, verticalAlign: 'middle' }}>
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  );
}

// Map suggestedAction values to short button labels (non-personalised fallback)
const ACTION_LABEL = {
  'Chase payment': 'Chase payment',
  'Send a gentle reminder': 'Send reminder',
  'Follow up on proposal': 'Follow up',
  'Discuss renewal or next package': 'Discuss renewal',
};

function getButtonLabel(nudge) {
  if (nudge.suggestedAction === 'Schedule a check-in') {
    return `Book a follow-up meeting with ${nudge.contactName}`;
  }
  return ACTION_LABEL[nudge.suggestedAction] || nudge.suggestedAction;
}

// Map nudge type → the view to navigate to on card click
const NUDGE_TARGET_VIEW = {
  overdue_invoice:      'Invoicing',
  unsigned_contract:    'Contracts',
  unanswered_proposal:  'Contacts',
  no_contact:           'Contacts',
  package_expiring:     'Contacts',
};

function dispatchAction(nudge) {
  const { contactName, suggestedAction } = nudge;

  if (suggestedAction === 'Schedule a check-in') {
    window.dispatchEvent(new CustomEvent('aidey:open-chat', {
      detail: { message: `Book a follow-up meeting with ${contactName}` },
    }));
    return;
  }

  const messages = {
    'Chase payment': `Draft a payment chase email for ${contactName}`,
    'Send a gentle reminder': `Draft a follow-up for ${contactName} about their unsigned contract`,
    'Follow up on proposal': `Draft a follow-up for ${contactName} about their unsigned contract`,
    'Discuss renewal or next package': `Draft a renewal message for ${contactName}`,
  };
  const message = messages[suggestedAction] || `Help me follow up with ${contactName}`;
  window.dispatchEvent(new CustomEvent('aidey:open-chat', { detail: { message } }));
}

/**
 * FollowUpPanel
 *
 * Fetches backend nudges and renders a card per nudge, grouped by urgency.
 * Props:
 *   hideWhenEmpty — if true, renders null instead of the empty state (Dashboard use case)
 *   onNavigate(view) — optional; if provided, cards become clickable deep-links
 */
export default function FollowUpPanel({ hideWhenEmpty = false, onNavigate }) {
  const [nudges, setNudges] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/follow-up/nudges')
      .then(r => r.json())
      .then(data => setNudges(Array.isArray(data.nudges) ? data.nudges : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visible = nudges.filter(n => !dismissed.has(`${n.contactId}:${n.type}`));

  if (loading) {
    if (hideWhenEmpty) return null;
    return (
      <div className="dashboard-box" style={{ fontFamily: "'Raleway', sans-serif" }}>
        <h3 style={{ margin: '0 0 0.75rem' }}>Follow-up needed</h3>
        <p style={{ margin: 0, color: '#bbb', fontSize: '0.9rem' }}>Loading…</p>
      </div>
    );
  }

  if (visible.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <div className="dashboard-box" style={{ fontFamily: "'Raleway', sans-serif" }}>
        <h3 style={{ margin: '0 0 0.75rem' }}>Follow-up needed</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#27ae60', fontSize: '0.92rem' }}>
          <span style={{ fontSize: '1.1rem' }}>✅</span>
          All clients are up to date
        </div>
      </div>
    );
  }

  const grouped = { high: [], medium: [], low: [] };
  for (const n of visible) {
    if (grouped[n.urgency]) grouped[n.urgency].push(n);
  }

  const sections = [
    { key: 'high', color: URGENCY_COLOR.high },
    { key: 'medium', color: URGENCY_COLOR.medium },
    { key: 'low', color: URGENCY_COLOR.low },
  ].filter(s => grouped[s.key].length > 0);

  return (
    <div className="dashboard-box" style={{ fontFamily: "'Raleway', sans-serif" }}>
      <h3 style={{ margin: '0 0 0.75rem' }}>Follow-up needed</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {sections.map(section =>
          grouped[section.key].map(nudge => {
            const key        = `${nudge.contactId}:${nudge.type}`;
            const targetView = NUDGE_TARGET_VIEW[nudge.type];
            const clickable  = !!(targetView && onNavigate);
            return (
              <div
                key={key}
                onClick={clickable ? () => onNavigate(targetView) : undefined}
                title={clickable ? `Open ${targetView}` : undefined}
                style={{
                  padding: '0.6rem 0.75rem',
                  borderRadius: 8,
                  border: '1px solid #e0e0e0',
                  borderLeft: `3px solid ${section.color}`,
                  background: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.3rem',
                  cursor: clickable ? 'pointer' : 'default',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={clickable ? e => { e.currentTarget.style.background = '#f7f9fc'; } : undefined}
                onMouseLeave={clickable ? e => { e.currentTarget.style.background = '#fff'; } : undefined}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <NudgeWarningIcon color={section.color} />
                    <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a1a' }}>{nudge.contactName}</span>
                    {clickable && (
                      <span style={{ fontSize: '0.72rem', color: '#bbb' }}>↗</span>
                    )}
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); setDismissed(prev => new Set([...prev, key])); }}
                    title="Dismiss"
                    style={{ background: 'none', border: 'none', color: '#bbb', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, padding: 0, flexShrink: 0 }}
                  >
                    ×
                  </button>
                </div>
                <div style={{ fontSize: '0.82rem', color: '#555', paddingLeft: '1.3rem' }}>{nudge.reason}</div>
                <div style={{ paddingLeft: '1.3rem' }}>
                  <button
                    onClick={e => { e.stopPropagation(); dispatchAction(nudge); }}
                    style={{
                      background: '#f0f5f9',
                      border: 'none',
                      borderRadius: 6,
                      padding: '0.3rem 0.65rem',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: '#0C2D48',
                      cursor: 'pointer',
                      fontFamily: "'Raleway', sans-serif",
                    }}
                  >
                    {getButtonLabel(nudge)}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
